/**
 * Undo GROUPING for chunked bulk writes (F-90) — the mechanism, extracted from
 * BoardSession so it is testable without a WebSocket.
 *
 * The problem it solves: F-90 wants a 10k-row import to be ONE undo step and to
 * not freeze the tab. A Yjs transaction is synchronous, so those two cannot
 * both be satisfied by one transaction — the import writes ~500 rows per
 * transaction and yields between them. Yjs merges consecutive tracked
 * transactions into the top undo-stack item only while they land within
 * `captureTimeout` (300 ms) of each other, and a chunked write's gaps are at
 * the mercy of whatever else the tab is doing: one slow frame and Ctrl+Z undoes
 * 500 of 10,000 rows. So the merge window is opened explicitly for the group's
 * duration instead of hoped for.
 */

/** The slice of `Y.UndoManager` this needs — the whole reason it is testable. */
export interface UndoCapture {
  /** Merge window in ms. Yjs reads it per transaction, so it can be changed. */
  captureTimeout: number;
  /** Force the next transaction to open a NEW stack item. */
  stopCapturing(): void;
}

/**
 * Open a group. Everything written until the returned `end()` becomes one
 * undo-stack item; the edits immediately before and after stay their own.
 *
 * NESTING IS COUNTED, and that is the whole subtlety. Groups CAN overlap: both
 * callers are async (a chunked import, a large paste), and a Ctrl+V during an
 * import arrives on a window listener that no modal blocks. With naive
 * save/restore the overlap corrupts the timeout PERMANENTLY — the outer group's
 * `end()` restores 300, then the inner one restores the Infinity it captured,
 * and from then on every edit in the session merges into a single undo item.
 * The real value is therefore saved once at depth 0 and restored once at depth
 * 0; overlapping groups collapse into one undo item, which is the honest
 * outcome for two bulk writes that genuinely interleaved.
 */
export function createUndoGrouper(manager: UndoCapture): () => () => void {
  let depth = 0;
  let saved = 0;

  return function begin(): () => void {
    if (depth === 0) {
      saved = manager.captureTimeout;
      // The edit BEFORE the group must not be swallowed into it.
      manager.stopCapturing();
      manager.captureTimeout = Number.POSITIVE_INFINITY;
    }
    depth++;
    let ended = false;
    return function end(): void {
      if (ended) return; // idempotent: a caller's `finally` may double-fire
      ended = true;
      depth--;
      if (depth > 0) return;
      manager.captureTimeout = saved;
      // The first edit AFTER the group starts a fresh item.
      manager.stopCapturing();
    };
  };
}
